import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./en.json";

export const LANGS = [
  { code: "en", native: "English", rtl: false },
  { code: "ar", native: "العربية", rtl: true },
  { code: "hi", native: "हिन्दी", rtl: false },
  { code: "mr", native: "मराठी", rtl: false },
  { code: "es", native: "Español", rtl: false },
  { code: "de", native: "Deutsch", rtl: false },
  { code: "zh", native: "中文", rtl: false },
  { code: "ur", native: "اردو", rtl: true },
];

export const isRtl = (lng) => Boolean(LANGS.find((l) => l.code === lng)?.rtl);

i18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: "en",
  fallbackLng: "en", // missing key -> English, never raw key ids
  interpolation: { escapeValue: false },
  returnEmptyString: false,
});

/* Arabic/Urdu webfonts are fetched ONLY when those languages activate.
   Pairing: IBM Plex Sans Arabic + Cairo (ar); Noto Naskh Arabic (ur). */
const FONT_LINKS = {
  ar: "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@400;500;600;700&family=Cairo:wght@600;700;800&display=swap",
  ur: "https://fonts.googleapis.com/css2?family=Noto+Naskh+Arabic:wght@400;500;600;700&display=swap",
};

function ensureFonts(lng) {
  const href = FONT_LINKS[lng];
  if (!href || document.getElementById(`i18n-font-${lng}`)) return;
  const link = document.createElement("link");
  link.id = `i18n-font-${lng}`;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

function applyDir(lng) {
  ensureFonts(lng);
  document.documentElement.dir = isRtl(lng) ? "rtl" : "ltr";
  document.documentElement.lang = lng;
}

export async function setLanguage(lng) {
  ensureFonts(lng);
  if (lng !== "en" && !i18n.hasResourceBundle(lng, "translation")) {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/i18n/${lng}`);
    if (!res.ok) throw new Error("catalog_not_ready");
    const data = await res.json();
    i18n.addResourceBundle(lng, "translation", data.catalog, true, true);
  }
  await i18n.changeLanguage(lng);
  localStorage.setItem("aetria_lang", lng);
  applyDir(lng);
}

// restore per-browser choice before first paint (English needs no fetch)
const saved = localStorage.getItem("aetria_lang") || "en";
applyDir(saved);
if (saved !== "en") setLanguage(saved).catch(() => applyDir("en"));

/* Background prefetch: warm every language bundle shortly after boot so
   switching is INSTANT (no loading flash, no network wait). Staggered,
   best-effort, and skipped for bundles that are already loaded. */
let prefetched = false;
export function prefetchCatalogs() {
  if (prefetched) return;
  prefetched = true;
  LANGS.filter((l) => l.code !== "en").forEach(({ code }, i) => {
    setTimeout(async () => {
      if (i18n.hasResourceBundle(code, "translation")) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/i18n/${code}`);
        if (!res.ok) return;
        const data = await res.json();
        i18n.addResourceBundle(code, "translation", data.catalog, true, true);
      } catch { /* background best-effort */ }
    }, 350 * i);
  });
}
setTimeout(prefetchCatalogs, 2500); // after first paint & critical requests

export default i18n;
